{
  "_provenance": {
    "ns": "settings",
    "source": "src/pages/SettingsPage.tsx + src/components/settings/LanguageSelect.tsx + src/components/layout/useChooseLanguage.ts"
  },
  "account": {
    "patient": {
      "childName": "Child’s name",
      "cycle": {
        "current": "Current: day {{day}}",
        "currentOf": "Current: day {{day}} of {{length}}",
        "helper": "Update these when a new cycle begins. {{companion}} uses the start date to compute the current day in cycle.",
        "length": "Length (days)",
        "number": "Cycle #",
        "started": "Started",
        "title": "Treatment cycle (optional)"
      },
      "dateOfBirth": "Date of birth",
      "description": "{{companion}} uses this to personalise the experience. Cycle info is optional, and updating it as a new cycle starts keeps the day count accurate.",
      "descriptionMemorial": "{{companion}} uses this to personalise the experience.",
      "diagnosis": "Diagnosis",
      "diagnosisPlaceholder": "e.g. ALL",
      "hospital": "Hospital / clinic",
      "phasePlaceholder": "Select phase",
      "phases": {
        "active_treatment": "Active treatment",
        "diagnosis": "Diagnosis",
        "maintenance": "Maintenance",
        "off_treatment": "Off treatment",
        "other": "Other",
        "survivorship": "Survivorship"
      },
      "title": "Patient",
      "treatmentPhase": "Treatment phase"
    },
    "privacy": {
      "closeAction": "Close account",
      "closeDescription": "Schedule your account to close after a {{days}}-day grace period.",
      "closeReview": "Review closure",
      "closeScheduledDescription": "Your account will close on {{date}}. You can cancel any time before then.",
      "closeTitle": "Close your account",
      "conversationAccess": "Let TRF’s program leads read your conversations with {{companion}}",
      "conversationAccessDescription": "They read them only to understand how to help your family. Other TRF staff can’t see them, and neither can TRF’s AI assistant. The engineers who build this app may see a conversation while fixing a problem, and they keep what they see private. You can turn this off at any time.",
      "deleteAction": "Delete",
      "deleteConfirm": "Yes, request deletion",
      "deleteDescription": "Request permanent deletion of your account and all data",
      "deleteDialogDescription": "This sends TRF a request to permanently delete your account and data. Nothing is deleted right away — a TRF team member will follow up with you to confirm before anything is removed.",
      "deleteDialogTitle": "Request deletion of your data?",
      "deleteRequested": "Requested",
      "deleteTitle": "Delete your data",
      "description": "Control who sees your information and manage your data.",
      "exportAction": "Request export",
      "exportDescription": "Ask for a copy of your TRF data. A TRF team member will follow up.",
      "exportRequested": "Requested",
      "exportTitle": "Export your data",
      "requestedOn": "Requested on {{date}}. A TRF team member will follow up.",
      "title": "Privacy & Data"
    },
    "profile": {
      "description": "Update your personal information.",
      "title": "Profile"
    },
    "security": {
      "confirmPassword": "Confirm your password to turn off two-factor",
      "description": "Manage your account security settings.",
      "enterCode": "Enter the 6-digit code sent to your email",
      "mfaOff": "Add an extra layer of security to your account.",
      "mfaOn": "A code goes to your email or phone each time you sign in.",
      "mfaTitle": "Two-Factor Authentication",
      "passwordPlaceholder": "Current password",
      "title": "Security",
      "turnOff": "Turn off two-factor",
      "verify": "Verify",
      "verifying": "Verifying…"
    },
    "timezone": {
      "browser": "Use my browser’s time zone",
      "groups": {
        "africaMiddleEast": "Africa & Middle East",
        "asia": "Asia",
        "europe": "Europe",
        "latinAmerica": "Latin America",
        "pacific": "Pacific",
        "us": "United States & Canada"
      },
      "helper": "Auto-detected from your browser. Used for scheduling tasks and events.",
      "helperStaff": "Times you read in the platform, such as when an application came in or when the AI review ran, show in this zone. Use your browser’s zone to follow wherever you are. Deadlines and code expiries still end at midnight Pacific.",
      "label": "Timezone",
      "placeholder": "Select your timezone",
      "zones": {
        "adelaide": "Adelaide",
        "anchorage": "Alaska",
        "auckland": "Auckland, Wellington",
        "bangkok": "Bangkok, Jakarta",
        "berlin": "Berlin, Frankfurt",
        "bogota": "Bogota, Lima",
        "buenosAires": "Buenos Aires",
        "cairo": "Cairo",
        "caracas": "Caracas",
        "chicago": "Central Time (US & Canada)",
        "denver": "Mountain Time (US & Canada)",
        "dhaka": "Dhaka",
        "dubai": "Dubai, Abu Dhabi",
        "fiji": "Fiji",
        "halifax": "Atlantic Time (Canada)",
        "helsinki": "Helsinki, Riga, Tallinn",
        "honolulu": "Hawaii",
        "honoluluAlsoUs": "Honolulu",
        "jerusalem": "Jerusalem, Tel Aviv",
        "johannesburg": "Johannesburg",
        "karachi": "Karachi, Islamabad",
        "kiev": "Kyiv",
        "kolkata": "Mumbai, New Delhi",
        "lagos": "Lagos, Accra",
        "london": "London, Dublin, Lisbon",
        "losAngeles": "Pacific Time (US & Canada)",
        "madrid": "Madrid, Barcelona",
        "mexicoCity": "Mexico City",
        "moscow": "Moscow",
        "nairobi": "Nairobi",
        "newYork": "Eastern Time (US & Canada)",
        "paris": "Paris, Brussels, Amsterdam",
        "perth": "Perth",
        "phoenix": "Arizona (no DST)",
        "riyadh": "Riyadh, Doha",
        "rome": "Rome, Milan",
        "santiago": "Santiago",
        "saoPaulo": "Brasilia",
        "seoul": "Seoul",
        "shanghai": "Beijing, Shanghai, Hong Kong",
        "singapore": "Singapore, Kuala Lumpur",
        "stJohns": "Newfoundland",
        "stockholm": "Stockholm, Oslo, Copenhagen",
        "sydney": "Sydney, Melbourne",
        "tehran": "Tehran",
        "tokyo": "Tokyo, Osaka",
        "warsaw": "Warsaw, Prague, Vienna"
      }
    }
  },
  "appearance": {
    "display": {
      "description": "Control how the sidebar behaves.",
      "left": "Left",
      "modes": {
        "auto": {
          "description": "Icon rail on medium screens, full sidebar on desktop",
          "label": "Auto (default)"
        },
        "collapsed": {
          "description": "Toggle button to expand or collapse at any width",
          "label": "Collapsible"
        },
        "hamburger": {
          "description": "Sidebar always opens as an overlay",
          "label": "Hamburger only"
        }
      },
      "right": "Right",
      "sideDescription": "On phones and small screens, put the menu on your dominant-hand side for easier one-handed reach. Larger screens keep the sidebar on the left.",
      "sideTitle": "Sidebar side",
      "title": "Display"
    },
    "theme": {
      "dark": "Dark",
      "description": "Choose your preferred color theme.",
      "light": "Light",
      "system": "System",
      "title": "Appearance"
    }
  },
  "companion": {
    "autoTasks": {
      "description": "Let {{companion}} stage the small follow-through tasks you’d otherwise have to write down — refills, recovery checklists, appointment prep. Each task is tagged \"Suggested by {{companion}}\" so you can dismiss anything that doesn’t fit.",
      "enable": "Enable {{companion}} auto-tasks",
      "enableHelper": "Off by default. Turn on to let the rules below run.",
      "prep": "Appointment prep",
      "prepExample": "24h before any appointment that needs prep, with the prep notes you’ve already entered.",
      "recovery": "Post-chemo recovery checklist",
      "recoveryExample": "On the first day of each new cycle: a short checklist for the days ahead, like easy meals and a comfortable place to rest.",
      "refill": "Refill prep",
      "refillExample": "A reminder two days before a refill date you’ve entered, so nothing runs out.",
      "title": "{{companion}} auto-tasks"
    },
    "conversations": {
      "description": "Every thread you have started with {{companion}}, newest first.",
      "title": "Conversations"
    },
    "voice": {
      "description": "Choose how {{companion}} sounds when speaking to you.",
      "faster": "Faster",
      "loading": "Loading voice settings…",
      "normal": "Normal",
      "slower": "Slower",
      "speed": "Speed: {{speed}}",
      "speedAria": "Speaking speed",
      "title": "{{companion}}’s Voice"
    }
  },
  "language": {
    "description": "The language this app shows you. The companion’s voice language is a separate choice under Companion.",
    "heading": "Language",
    "helper": "Languages TRF has not reviewed yet are marked machine-translated.",
    "label": "Language",
    "saveFailed": "Couldn’t save your language",
    "saveFailedDescription": "It applies for this visit only. Please try again.",
    "staffEnglishOnly": "Staff and admin pages are English-only. Your saved language still applies on parent pages."
  },
  "lede": "Privacy, notifications, and account security.",
  "legal": {
    "description": "The documents you have agreed to, and when.",
    "heading": "Legal",
    "link": "Your agreements"
  },
  "notifications": {
    "autoAlert": {
      "claimStress": "If a reimbursement gets stuck",
      "claimStressOn": "TRF hears about it when a reimbursement is turned down or stops moving.",
      "description": "Choose when TRF hears about something without you sending a message, so your case manager can check in. Off means TRF won’t hear about it that way.",
      "lifecycle": "Around your child’s birthday and other dates",
      "lifecycleOn": "TRF reaches out around dates that matter, like your child’s birthday.",
      "off": "TRF won’t hear about this unless you tell us.",
      "title": "When TRF may check in without you asking"
    },
    "communication": {
      "description": "Choose how you’d like to hear from {{companion}} and TRF.",
      "email": "Email notifications",
      "emailDescription": "Task updates, circle activity, and important alerts",
      "push": "Push notifications",
      "pushBrowser": "Browser notifications when you’re online",
      "pushNative": "Notifications on this device. Turn off to stop all categories at once.",
      "title": "Communication preferences"
    },
    "pushCategories": {
      "description": "Choose which alerts buzz your phone. Turning one off here only stops the buzz — the notification still shows in your inbox in the app. Whether TRF is told at all is up to the switches further down.",
      "grantDecision": "Grant and reimbursement updates",
      "grantDecisionDescription": "When there’s a decision on a grant request for your family, or TRF needs more information about a reimbursement",
      "handback": "Conversation handed back",
      "handbackDescription": "When your case manager hands the conversation back to {{companion}}",
      "handoffNudge": "Handoff nudges",
      "handoffNudgeDescription": "When {{companion}} detects you might want a check-in",
      "taskDue": "Task reminders",
      "taskDueDescription": "When a task you own is coming due",
      "title": "Push categories",
      "trferClaimed": "A case manager joined",
      "trferClaimedDescription": "When a case manager claims your conversation",
      "trferMessage": "New case manager message",
      "trferMessageDescription": "When a case manager sends you a message"
    }
  },
  "tabs": {
    "account": "Account",
    "appearance": "Appearance",
    "appearanceShort": "Display",
    "companion": "Companion",
    "notifications": "Notifications",
    "notificationsShort": "Alerts"
  },
  "title": "Your <em>preferences</em>.",
  "toasts": {
    "alreadyOpenDescription": "Your earlier request is still open. A TRF team member will follow up.",
    "autoTasksFailed": "Couldn’t update {{companionName}} auto-tasks",
    "autoTasksFailedDescription": "The setting is unchanged. Please try again.",
    "deletionAlreadyOpen": "Deletion already requested",
    "deletionFailed": "Couldn’t submit your deletion request",
    "deletionSubmitted": "Deletion request submitted",
    "displaySaved": "Display settings saved",
    "exportAlreadyOpen": "Export already requested",
    "exportFailed": "Couldn’t request your data export",
    "exportRequested": "Export requested",
    "loadFailed": "Couldn’t load your settings",
    "loadFailedDescription": "Refresh the page to try again.",
    "mfaCodeFailed": "Couldn’t send the code",
    "mfaDisabled": "Two-factor disabled",
    "mfaDisableFailed": "Couldn’t turn off two-factor",
    "mfaDisableFailedDescription": "Check your password and try again.",
    "mfaEnabled": "Two-factor enabled",
    "mfaEnabledDescription": "You will be asked for a code each time you sign in.",
    "mfaInvalidCode": "Invalid code",
    "notSavedDescription": "Your changes weren’t saved. Please try again.",
    "patientFailed": "Couldn’t save the patient details",
    "patientSaved": "Patient info saved",
    "profileFailed": "Couldn’t save your profile",
    "profileSaved": "Profile saved",
    "recordedDescription": "Your request was recorded. A TRF team member will follow up.",
    "settingsFailed": "Couldn’t save your settings",
    "settingsSaved": "Settings saved",
    "tryAgainDescription": "Please try again."
  }
}
